/**
 * F-tech-signup (CEO 2026-09-20) — porta PÚBLICA: o técnico se cadastra
 * sozinho em app.eurostormdebosselage.com e cai na fila do escritório.
 * Nada aqui cria conta: até a aprovação não existe login nem cadastro de
 * técnico. Por isso a página não pede senha — ela chega por e-mail depois.
 */

import { useState } from "react";
import {
  AlertError,
  AlertSuccess,
  AuthBrand,
  AuthShell,
  FieldLabel,
  GhostLink,
  PillInput,
  PrimaryButton,
} from "../components/auth/AuthShell.js";
import { useI18n } from "../i18n/I18nProvider.js";
import { useT } from "../i18n/index.js";
import type { Locale } from "../i18n/messages.js";
import { trpc } from "../lib/trpc.js";

const PAISES = [
  { code: "FR", label: "France" },
  { code: "BR", label: "Brasil" },
  { code: "PT", label: "Portugal" },
  { code: "ES", label: "España" },
  { code: "BE", label: "Belgique" },
  { code: "CH", label: "Suisse" },
  { code: "IT", label: "Italia" },
  { code: "DE", label: "Deutschland" },
] as const;

const seletor: React.CSSProperties = {
  width: "100%",
  height: 52,
  borderRadius: 999,
  border: "1px solid rgba(255,255,255,0.14)",
  background: "rgba(255,255,255,0.04)",
  color: "#F5F7FA",
  padding: "0 20px",
  fontFamily: "var(--font-body)",
  fontSize: 15,
  outline: "none",
  marginBottom: 14,
};

/**
 * O menu nativo do <select> é desenhado pelo SISTEMA, com fundo branco: as
 * opções herdavam o texto claro do campo e ficavam invisíveis (o CEO abriu a
 * lista de idiomas e não viu nada). Cor explícita e escura nas opções.
 */
const opcao: React.CSSProperties = { color: "#0F172A", background: "#FFFFFF" };

export default function TechnicianSignup() {
  const t = useT();
  const { locale, setLocale } = useI18n();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState("FR");
  const [role, setRole] = useState<"debosselage" | "montage">("debosselage");
  // F-tech-docs-a1 — onde ele RESIDE decide os documentos: residente na
  // França manda o da URSSAF; estabelecido fora manda o certificado A1.
  const [residence, setResidence] = useState("FR");
  const [foco, setFoco] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pronto, setPronto] = useState(false);

  const enviar = trpc.technicianApplications.submit.useMutation({
    onSuccess: () => setPronto(true),
    onError: (e) => setErro(e.message),
  });

  if (pronto) {
    return (
      <AuthShell>
        <div className="w-full max-w-[430px]">
          <AuthBrand subtitle={t("techSignup.subtitle")} />
          <AlertSuccess>{t("techSignup.successTitle")}</AlertSuccess>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 14,
              color: "var(--text-secondary)",
              lineHeight: 1.6,
              marginTop: 14,
            }}
          >
            {t("techSignup.successBody")}
          </p>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 13,
              color: "var(--text-muted)",
              lineHeight: 1.6,
              marginTop: 14,
            }}
          >
            {t("techSignup.docsNotice")}
          </p>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <GhostLink href="/login">{t("techSignup.backToLogin")}</GhostLink>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="w-full max-w-[430px]">
        <AuthBrand subtitle={t("techSignup.subtitle")} />
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setErro(null);
            if (!name.trim() || !email.trim() || !phone.trim() || !country) {
              setErro(t("techSignup.errorRequired"));
              return;
            }
            enviar.mutate({
              name: name.trim(),
              email: email.trim(),
              phone: phone.trim(),
              locale: locale as "fr" | "en" | "pt" | "es" | "uk",
              countryCode: country,
              serviceRole: role,
              residenceCountry: residence,
            });
          }}
        >
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 14,
              color: "var(--text-secondary)",
              textAlign: "center",
              marginBottom: 20,
              lineHeight: 1.5,
            }}
          >
            {t("techSignup.intro")}
          </p>

          {erro && <AlertError>{erro}</AlertError>}

          <FieldLabel htmlFor="nome">{t("techSignup.nameLabel")}</FieldLabel>
          <PillInput
            id="nome"
            value={name}
            onChange={setName}
            focused={foco === "nome"}
            onFocus={() => setFoco("nome")}
            onBlur={() => setFoco(null)}
            disabled={enviar.isPending}
            required
            autoFocus
            autoComplete="name"
          />

          <FieldLabel htmlFor="email">{t("techSignup.emailLabel")}</FieldLabel>
          <PillInput
            id="email"
            type="email"
            value={email}
            onChange={setEmail}
            focused={foco === "email"}
            onFocus={() => setFoco("email")}
            onBlur={() => setFoco(null)}
            disabled={enviar.isPending}
            required
            autoComplete="email"
          />
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--text-muted)",
              margin: "-8px 0 14px 20px",
            }}
          >
            {t("techSignup.emailHint")}
          </p>

          <FieldLabel htmlFor="tel">{t("techSignup.phoneLabel")}</FieldLabel>
          <PillInput
            id="tel"
            value={phone}
            onChange={setPhone}
            focused={foco === "tel"}
            onFocus={() => setFoco("tel")}
            onBlur={() => setFoco(null)}
            disabled={enviar.isPending}
            required
            autoComplete="tel"
          />

          <FieldLabel htmlFor="funcao">{t("techSignup.roleLabel")}</FieldLabel>
          <select
            id="funcao"
            value={role}
            onChange={(e) => setRole(e.target.value as "debosselage" | "montage")}
            style={seletor}
            disabled={enviar.isPending}
          >
            <option value="debosselage" style={opcao}>
              {t("techSignup.roleDebosselage")}
            </option>
            <option value="montage" style={opcao}>
              {t("techSignup.roleMontage")}
            </option>
          </select>

          <FieldLabel htmlFor="idioma">{t("techSignup.langLabel")}</FieldLabel>
          <select
            id="idioma"
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            style={seletor}
            disabled={enviar.isPending}
          >
            <option value="pt" style={opcao}>
              Português
            </option>
            <option value="fr" style={opcao}>
              Français
            </option>
            <option value="en" style={opcao}>
              English
            </option>
            <option value="es" style={opcao}>
              Español
            </option>
            <option value="uk" style={opcao}>
              Українська
            </option>
          </select>

          <FieldLabel htmlFor="pais">{t("techSignup.countryLabel")}</FieldLabel>
          <select
            id="pais"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            style={seletor}
            disabled={enviar.isPending}
          >
            {PAISES.map((p) => (
              <option key={p.code} value={p.code} style={opcao}>
                {p.label}
              </option>
            ))}
          </select>

          <FieldLabel htmlFor="residencia">{t("techSignup.residenceLabel")}</FieldLabel>
          <select
            id="residencia"
            value={residence}
            onChange={(e) => setResidence(e.target.value)}
            style={seletor}
            disabled={enviar.isPending}
          >
            {PAISES.map((p) => (
              <option key={p.code} value={p.code} style={opcao}>
                {p.label}
              </option>
            ))}
          </select>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--text-muted)",
              margin: "-8px 0 14px 20px",
            }}
          >
            {t("techSignup.residenceHint")}
          </p>

          <PrimaryButton disabled={enviar.isPending}>
            {enviar.isPending ? t("techSignup.sending") : t("techSignup.submitBtn")}
          </PrimaryButton>

          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: 12,
              color: "var(--text-muted)",
              lineHeight: 1.6,
              marginTop: 18,
            }}
          >
            {t("techSignup.docsNotice")}
          </p>

          <div style={{ marginTop: 18, textAlign: "center" }}>
            <GhostLink href="/login">{t("techSignup.backToLogin")}</GhostLink>
          </div>
        </form>
      </div>
    </AuthShell>
  );
}
